import { TextPlaneInputs } from '@/_models/virtualStaging/AIAvater/TextPlane/component/textPlaneTypes';
import {
  makeTextPlaneComponent,
  textPlaneComponentType,
} from '@/_models/virtualStaging/AIAvater/TextPlane/component/TextPlane.component';

const initTextPlaneObject = async (options: Partial<TextPlaneInputs>, showcaseTemp) => {
  try {
    await showcaseTemp.showcase.Scene.register(textPlaneComponentType, makeTextPlaneComponent);
    const modelNode: any = await showcaseTemp.mpSDK.Scene.createNode();
    const component = await modelNode.addComponent(textPlaneComponentType, options);
    await modelNode.start();
    return [modelNode, component];
  } catch (e) {
    console.error(e);
    return [];
  }
};

/**
 * Simple TextPlane button that shows near AI Avatar objects
 * - Visible by default
 * - Hides on click
 * - Can be triggered to show again
 */
export class TextPlaneButton {
  public component: any = null;

  private textPlaneNode: any = null;

  private isVisible = true;

  private showcaseTemp: any;

  private aiAvatarObject: any;

  constructor(showcaseTemp: any, aiAvatarObject: any) {
    this.showcaseTemp = showcaseTemp;
    this.aiAvatarObject = aiAvatarObject;
  }

  async init() {
    // Calculate position near the AI Avatar
    const position = {
      x: this.aiAvatarObject.positionX - 0.1,
      y: this.aiAvatarObject.positionY + 1, // Above avatar
      z: this.aiAvatarObject.positionZ - 0.4, // Further in front
    };

    // Use AI Avatar's rotation for the TextPlane
    const rotation = {
      x: this.aiAvatarObject.rotationX || 0,
      y: this.aiAvatarObject.rotationY || 0,
      z: this.aiAvatarObject.rotationZ || 0,
    };

    const options: Partial<TextPlaneInputs> = {
      position,
      rotation,
      visible: true,
      text: 'Talk to Avatar',
      scale: 0.7, // Scale down to 90% for more compact size
      canvasWidth: 288, // 2x resolution for crisp rendering
      canvasHeight: 76, // 2x resolution for crisp rendering
      planeWidth: 1.44, // Keep same physical size in 3D space
      planeHeight: 0.38, // Keep same physical size in 3D space
      backgroundColor: 'rgba(107, 114, 128, 0.8)', // Semi-transparent dark gray background
      textColor: '#ffffff',
      fontSize: 24, // 2x font size for higher resolution
      fontFamily: 'Inter',
      badgeText: 'AI',
      badgeBackgroundColor: '#ffffff', // White badge background
      badgeTextColor: '#000000',
      badgeFontSize: 20, // 2x badge font size for higher resolution
    };

    const [modelNode, component] = await initTextPlaneObject(options, this.showcaseTemp);
    this.textPlaneNode = modelNode;
    this.component = component;

    return this;
  }

  show() {
    if (this.component) {
      this.component.setVisible(true);
      this.isVisible = true;
      console.log('TextPlane button shown');
    }
  }

  hide() {
    if (this.component) {
      this.component.setVisible(false);
      this.isVisible = false;
      console.log('TextPlane button hidden');
    }
  }

  toggle() {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  setText(text: string) {
    if (this.component) {
      this.component.setText(text);
    }
  }

  updatePositionAndRotation() {
    if (this.component) {
      // Update position
      const newPosition = {
        x: this.aiAvatarObject.positionX || 0,
        y: (this.aiAvatarObject.positionY || 0) + 1.5,
        z: (this.aiAvatarObject.positionZ || 0) - 0.4
      };

      // Update rotation based on AI Avatar
      const newRotation = {
        x: this.aiAvatarObject.rotationX || 0,
        y: this.aiAvatarObject.rotationY || 0,
        z: this.aiAvatarObject.rotationZ || 0
      };

      // Update the component inputs
      this.component.inputs.position = newPosition;
      this.component.inputs.rotation = newRotation;

      console.log(`Updated TextPlane position and rotation for AI Avatar ${this.aiAvatarObject.id}`);
    }
  }

  getIsVisible() {
    return this.isVisible;
  }

  destroy() {
    if (this.textPlaneNode) {
      this.textPlaneNode.stop();
    }
  }

  private getAvatarCenter() {
    // Try to get the center from the 3D model if available
    if (this.aiAvatarObject.node?.model) {
      const THREE = this.showcaseTemp.three;
      const model = this.aiAvatarObject.node.model;

      try {
        // Calculate bounding box of the model
        const boundingBox = new THREE.Box3().setFromObject(model);
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);
        const size = new THREE.Vector3();
        boundingBox.getSize(size);

        return {
          center: { x: center.x, y: center.y, z: center.z },
          size: { x: size.x, y: size.y, z: size.z },
          boundingBox: boundingBox
        };
      } catch (error) {
        console.warn('Could not calculate bounding box for AI Avatar:', error);
      }
    }

    // Fallback to using position properties if model is not available
    return {
      center: {
        x: this.aiAvatarObject.positionX || 0,
        y: this.aiAvatarObject.positionY || 0,
        z: this.aiAvatarObject.positionZ || 0
      },
      size: { x: 1, y: 2, z: 1 }, // Estimated size
      boundingBox: null
    };
  }
}
